import { GetCars } from "api/services/car";
import {
  CreateVideo,
  DeleteVideo,
  GetVideos,
  UpdateVideo,
} from "api/services/videos";
import { SideBarTabs } from "constants/tabs";
import { formatDate, getPrice } from "functions/text";
import { useTabActive } from "hook/useTabActive";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IoEyeOffOutline,
  IoEyeOutline,
  IoPlayCircleOutline,
  IoRefresh,
  IoSearch,
  IoTrashOutline,
} from "react-icons/io5";
import { CarType, VideoPostStatusType, VideoPostType } from "types/loadedData";
import Menu from "components/common/Menu";
import { CommonStyles } from "styles/CommonStyles";
import { dashboardConfirm } from "i18n/dashboardLanguage";

const emptyForm = {
  title: "",
  description: "",
  status: "Published" as VideoPostStatusType,
  car: "",
  carLabel: "",
  thumbnailUrl: "",
  video: null as File | null,
};

const maxVideoBytes = 50 * 1024 * 1024;

function ShortVideosPage() {
  const [videos, setVideos] = useState<VideoPostType[]>([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<VideoPostType | null>(null);
  const [carSearch, setCarSearch] = useState("");
  const [carResults, setCarResults] = useState<CarType[]>([]);
  const [searchingCars, setSearchingCars] = useState(false);

  useTabActive(SideBarTabs.ShortVideos.name);

  const selectedFileName = useMemo(() => form.video?.name || "", [form.video]);

  const loadVideos = useCallback(async () => {
    const response = await GetVideos({
      setLoading,
      search,
      limit: 80,
    });
    if (response.status === 200) {
      setVideos(response.videos);
      setCount(response.count);
    }
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(loadVideos, 250);
    return () => clearTimeout(timeout);
  }, [loadVideos]);

  useEffect(() => {
    if (form.car) {
      setCarResults([]);
      return;
    }

    if (carSearch.trim().length < 2) {
      setCarResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      const response = await GetCars({
        setLoading: setSearchingCars,
        search: carSearch,
        limit: 8,
      });
      if (response.status === 200) setCarResults(response.cars || []);
    }, 250);

    return () => clearTimeout(timeout);
  }, [carSearch, form.car]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(null);
    setCarSearch("");
    setCarResults([]);
    setError("");
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setError("");
    if (file && file.size > maxVideoBytes) {
      setError("Video must be 50MB or smaller.");
      event.target.value = "";
      setForm((current) => ({ ...current, video: null }));
      return;
    }
    setForm((current) => ({ ...current, video: file }));
  };

  const selectCar = (car: CarType) => {
    const label = getCarLabel(car);
    setForm((current) => ({ ...current, car: car._id, carLabel: label }));
    setCarSearch(label);
    setCarResults([]);
  };

  const startEdit = (video: VideoPostType) => {
    const car = getVideoCar(video);
    const carLabel = car ? getCarLabel(car) : "";
    setEditing(video);
    setForm({
      title: video.title || "",
      description: video.description || "",
      status: video.status,
      car: car?._id || "",
      carLabel,
      thumbnailUrl: video.thumbnailUrl || "",
      video: null,
    });
    setCarSearch(carLabel);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!editing && !form.video) {
      setError("Choose an MP4 or MOV video before publishing.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        status: form.status,
        car: form.car,
        thumbnailUrl: form.thumbnailUrl.trim(),
        video: form.video,
      };

      const response = editing
        ? await UpdateVideo(editing._id, payload)
        : await CreateVideo(payload);

      if (response.status === 200 || response.status === 201) {
        if (response.video) {
          setVideos((current) => {
            const exists = current.some((item) => item._id === response.video?._id);
            if (exists) {
              return current.map((item) =>
                item._id === response.video?._id ? response.video : item
              ) as VideoPostType[];
            }
            return [response.video as VideoPostType, ...current];
          });
        }
        resetForm();
        loadVideos();
      } else {
        setError(response.message || "Could not save video.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (video: VideoPostType) => {
    const nextStatus = video.status === "Published" ? "Hidden" : "Published";
    const response = await UpdateVideo(video._id, { status: nextStatus });
    if (response.status === 200 && response.video) {
      setVideos((current) =>
        current.map((item) => (item._id === video._id ? response.video! : item))
      );
    }
  };

  const handleDelete = async (video: VideoPostType) => {
    if (!dashboardConfirm(`Delete "${video.title}"?`)) return;
    const response = await DeleteVideo(video._id);
    if (response.status === 200) {
      setVideos((current) => current.filter((item) => item._id !== video._id));
      setCount((current) => Math.max(current - 1, 0));
      if (editing?._id === video._id) resetForm();
    }
  };

  return (
    <div className={CommonStyles.pageWrapper}>
      <Menu />
      <div className={ClassName.header}>
        <div>
          <h1 className={ClassName.title}>Short Videos</h1>
          <p className={ClassName.subtitle}>
            Upload staff-curated BossCar short videos for the app Videos tab.
          </p>
        </div>
        <button className={ClassName.refreshButton} onClick={loadVideos}>
          <IoRefresh size={18} />
          Refresh
        </button>
      </div>

      <div className={ClassName.grid}>
        <section className={ClassName.formPanel}>
          <div className={ClassName.sectionHeader}>
            <IoPlayCircleOutline size={22} />
            <span>{editing ? "Edit video" : "Upload video"}</span>
          </div>
          <label className={ClassName.label}>
            Title
            <input
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({ ...current, title: event.target.value }))
              }
              className={ClassName.input}
              placeholder="Example: New arrivals at BossCar"
            />
          </label>
          <label className={ClassName.label}>
            Description
            <textarea
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              className={`${ClassName.input} min-h-[110px] resize-y`}
              placeholder="Short caption shown on the video feed"
            />
          </label>
          <label className={ClassName.label}>
            Video file {editing ? "(optional replacement)" : ""}
            <input
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov"
              onChange={handleFile}
              className={ClassName.file}
            />
            <span className={ClassName.hint}>
              MP4 or MOV only. Maximum 50MB. Recommended under 60 seconds.
              {selectedFileName ? ` Selected: ${selectedFileName}` : ""}
            </span>
          </label>
          <label className={ClassName.label}>
            Optional linked car
            <div className={ClassName.searchBox}>
              <IoSearch size={18} />
              <input
                value={carSearch}
                onChange={(event) => {
                  setCarSearch(event.target.value);
                  setForm((current) => ({ ...current, car: "", carLabel: "" }));
                }}
                className={ClassName.searchInput}
                placeholder="Search tag, make, model, year"
              />
            </div>
          </label>
          {searchingCars && <div className={ClassName.hint}>Searching cars...</div>}
          {carResults.length > 0 && (
            <div className={ClassName.carResults}>
              {carResults.map((car) => (
                <button
                  key={car._id}
                  className={ClassName.carResult}
                  onClick={() => selectCar(car)}
                >
                  {getCarLabel(car)}
                </button>
              ))}
            </div>
          )}
          {form.carLabel && (
            <button
              className={ClassName.clearLink}
              onClick={() => {
                setForm((current) => ({ ...current, car: "", carLabel: "" }));
                setCarSearch("");
              }}
            >
              Clear linked car: {form.carLabel}
            </button>
          )}
          <label className={ClassName.label}>
            Status
            <select
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  status: event.target.value as VideoPostStatusType,
                }))
              }
              className={ClassName.input}
            >
              <option value="Published">Published</option>
              <option value="Hidden">Hidden</option>
            </select>
          </label>
          {error && <div className={ClassName.error}>{error}</div>}
          <div className={ClassName.actions}>
            <button className={ClassName.primaryButton} onClick={handleSubmit}>
              {saving ? "Saving..." : editing ? "Save changes" : "Publish video"}
            </button>
            {editing && (
              <button className={ClassName.secondaryButton} onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </section>

        <section className={ClassName.listPanel}>
          <div className={ClassName.toolbar}>
            <div className={ClassName.searchBox}>
              <IoSearch size={18} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className={ClassName.searchInput}
                placeholder="Search title, description, status"
              />
            </div>
            <span className={ClassName.count}>{count} videos</span>
          </div>

          {loading ? (
            <div className="primaryLoader mx-auto my-10"></div>
          ) : videos.length === 0 ? (
            <div className={ClassName.empty}>No short videos yet.</div>
          ) : (
            <div className={ClassName.videoList}>
              {videos.map((video) => {
                const car = getVideoCar(video);
                return (
                  <article key={video._id} className={ClassName.videoCard}>
                    <video
                      src={video.videoUrl}
                      controls
                      preload="metadata"
                      className={ClassName.preview}
                    />
                    <div className={ClassName.videoBody}>
                      <div className={ClassName.videoTop}>
                        <div>
                          <h2 className={ClassName.videoTitle}>{video.title}</h2>
                          <p className={ClassName.videoMeta}>
                            {formatDate(video.createdAt)} | {video.status}
                          </p>
                        </div>
                        <span
                          className={
                            video.status === "Published"
                              ? ClassName.publishedBadge
                              : ClassName.hiddenBadge
                          }
                        >
                          {video.status}
                        </span>
                      </div>
                      {video.description && (
                        <p className={ClassName.videoDescription}>
                          {video.description}
                        </p>
                      )}
                      {car && (
                        <p className={ClassName.linkedCar}>Linked car: {getCarLabel(car)}</p>
                      )}
                      <div className={ClassName.videoActions}>
                        <button
                          className={ClassName.secondaryButton}
                          onClick={() => startEdit(video)}
                        >
                          Edit
                        </button>
                        <button
                          className={ClassName.secondaryButton}
                          onClick={() => handleToggleStatus(video)}
                        >
                          {video.status === "Published" ? (
                            <IoEyeOffOutline size={17} />
                          ) : (
                            <IoEyeOutline size={17} />
                          )}
                          {video.status === "Published" ? "Hide" : "Publish"}
                        </button>
                        <button
                          className={ClassName.dangerButton}
                          onClick={() => handleDelete(video)}
                        >
                          <IoTrashOutline size={17} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const getVideoCar = (video: VideoPostType) =>
  video.car && typeof video.car === "object" ? (video.car as CarType) : null;

const getCarMake = (car: CarType) => {
  const make = car.make as any;
  return typeof make === "object" ? make?.name || "" : make || "";
};

const getCarLabel = (car: CarType) => {
  const title = [car.tag, getCarMake(car), car.model, car.year]
    .filter(Boolean)
    .join(" ");
  return `${title}${car.price ? ` - ${getPrice(car.price)} Birr` : ""}`;
};

export default ShortVideosPage;

const ClassName = {
  header:
    "w-full flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-5",
  title: "text-[30px] font-bold text-primaryText",
  subtitle: "text-[15px] text-gray-500 mt-1",
  refreshButton:
    "h-[44px] px-4 rounded-[8px] bg-white border border-gray-200 flex items-center gap-2 font-semibold text-primaryText",
  grid: "grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5",
  formPanel: "bg-white rounded-[8px] border border-gray-100 p-5 flex flex-col gap-4",
  listPanel: "bg-white rounded-[8px] border border-gray-100 p-5 min-w-0",
  sectionHeader: "flex items-center gap-2 text-[18px] font-bold text-primaryText",
  label: "flex flex-col gap-2 text-[13px] font-semibold text-primaryText",
  input:
    "w-full h-[46px] rounded-[8px] border border-gray-200 px-3 outline-none focus:border-primary text-[15px] font-normal",
  file:
    "w-full rounded-[8px] border border-dashed border-gray-300 p-3 text-[14px]",
  hint: "text-[12px] text-gray-500 leading-5",
  searchBox:
    "h-[46px] rounded-[8px] border border-gray-200 bg-white px-3 flex items-center gap-2 min-w-0",
  searchInput: "w-full min-w-0 outline-none text-[15px]",
  carResults:
    "max-h-[210px] overflow-y-auto rounded-[8px] border border-gray-100 bg-secondary",
  carResult:
    "w-full text-left px-3 py-3 hover:bg-white border-b border-gray-100 text-[14px]",
  clearLink: "text-left text-[13px] text-primary font-semibold",
  error: "rounded-[8px] bg-red-50 text-primary px-3 py-2 text-[13px]",
  actions: "flex flex-wrap gap-3",
  primaryButton:
    "h-[44px] rounded-[8px] bg-primary px-5 text-white font-bold disabled:opacity-60",
  secondaryButton:
    "h-[40px] rounded-[8px] bg-secondary px-4 text-primaryText font-semibold flex items-center gap-2",
  dangerButton:
    "h-[40px] rounded-[8px] bg-red-50 px-4 text-primary font-semibold flex items-center gap-2",
  toolbar:
    "flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-5",
  count: "text-[13px] text-gray-500 font-semibold",
  empty: "rounded-[8px] bg-secondary p-8 text-center text-gray-500",
  videoList: "flex flex-col gap-4",
  videoCard:
    "rounded-[8px] border border-gray-100 bg-white p-3 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4",
  preview: "w-full aspect-video rounded-[8px] bg-primaryText object-cover",
  videoBody: "flex flex-col gap-3 min-w-0",
  videoTop: "flex items-start justify-between gap-3",
  videoTitle: "text-[18px] font-bold text-primaryText",
  videoMeta: "text-[12px] text-gray-500 mt-1",
  videoDescription: "text-[14px] text-gray-600 leading-6",
  linkedCar: "text-[13px] text-primaryText font-semibold",
  videoActions: "flex flex-wrap gap-2 mt-auto",
  publishedBadge:
    "rounded-full px-3 py-1 bg-green-50 text-green-700 text-[12px] font-bold",
  hiddenBadge:
    "rounded-full px-3 py-1 bg-gray-100 text-gray-600 text-[12px] font-bold",
};
